import { useState } from 'react';
import type { CustomerManagerUsageDailyResponse } from '@nadoutong/contracts';
import { formatCompact, formatUsdMicrosOrCentsAsCnyDisplay } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Panel, TBody, THead, Table, Td, Th, Tr } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';
import { alignPrevious, deltaPercent, formatDelta, leadModelByDate } from './usageMetrics.ts';
import { occurredThroughIndex, orderUsageDetailRows } from './usageWindows.ts';

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
const INITIAL_ROWS = 9;

const DAY_COPY = {
  title: 'customer_manager_360.usage.daily.title',
  compare: 'customer_manager_360.usage.daily.compare',
  nonzero: 'customer_manager_360.usage.daily.nonzero',
  date: 'customer_manager_360.usage.daily.date',
  prev_spend: 'customer_manager_360.usage.daily.prev_spend',
  empty: 'customer_manager_360.usage.daily.empty',
  expand: 'customer_manager_360.usage.daily.expand',
} as const satisfies Record<string, I18nKey>;

const HOUR_COPY = {
  title: 'customer_manager_360.usage.daily.title_hour',
  compare: 'customer_manager_360.usage.daily.compare_hour',
  nonzero: 'customer_manager_360.usage.daily.nonzero_hour',
  date: 'customer_manager_360.usage.daily.date_hour',
  prev_spend: 'customer_manager_360.usage.daily.prev_spend_hour',
  empty: 'customer_manager_360.usage.daily.empty_hour',
  expand: 'customer_manager_360.usage.daily.expand_hour',
} as const satisfies Record<string, I18nKey>;

export function UsageDailyTable({
  data,
  today,
  cnyPerUsd,
}: {
  readonly data: CustomerManagerUsageDailyResponse;
  readonly today: string;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const hourly = data.granularity === 'hour';
  const [compare, setCompare] = useState(true);
  const [nonzeroOnly, setNonzeroOnly] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const previousDays = alignPrevious(data.days, data.previous.days);
  const lead = leadModelByDate(data.by_day_model);
  const indexed = data.days.map((day, index) => ({ day, previous: previousDays[index], index }));
  const visible = nonzeroOnly
    ? indexed.filter(({ day }) => day.usd_charged_micros !== 0 || day.request_count !== 0)
    : indexed;
  const occurredIndex = occurredThroughIndex(
    visible.map(({ day }) => day.date),
    today,
  );
  const ordered = orderUsageDetailRows(visible, occurredIndex, hourly);
  const shown = expanded ? ordered : ordered.slice(0, INITIAL_ROWS);
  const hiddenCount = Math.max(0, ordered.length - shown.length);
  const copy = hourly ? HOUR_COPY : DAY_COPY;

  return (
    <Panel
      caption={t(copy.title)}
      count={visible.length}
      testId="customer-360-usage-daily"
      actions={
        <div className="flex flex-wrap gap-1">
          <Button
            variant={compare ? 'secondary' : 'ghost'}
            size="sm"
            className={compare ? 'bg-signal-dim text-signal-bright' : undefined}
            data-testid="customer-360-usage-daily-compare"
            onClick={() => {
              setCompare((value) => !value);
            }}
          >
            {t(copy.compare)}
          </Button>
          <Button
            variant={nonzeroOnly ? 'secondary' : 'ghost'}
            size="sm"
            className={nonzeroOnly ? 'bg-signal-dim text-signal-bright' : undefined}
            data-testid="customer-360-usage-daily-nonzero"
            onClick={() => {
              setNonzeroOnly((value) => !value);
            }}
          >
            {t(copy.nonzero)}
          </Button>
        </div>
      }
    >
      {visible.length === 0 ? (
        <p className="m-0 p-4 text-body text-paper-low">{t(copy.empty)}</p>
      ) : (
        <Table>
          <THead>
            <Tr interactive={false}>
              <Th>{t(copy.date)}</Th>
              <Th num>{t('customer_manager_360.usage.daily.requests')}</Th>
              <Th num>{t('customer_manager_360.usage.daily.input')}</Th>
              <Th num>{t('customer_manager_360.usage.daily.output')}</Th>
              <Th num>{t('customer_manager_360.usage.daily.spend')}</Th>
              {compare ? <Th num>{t(copy.prev_spend)}</Th> : null}
              {compare ? <Th num>{t('customer_manager_360.usage.daily.delta')}</Th> : null}
              <Th>{t('customer_manager_360.usage.daily.lead_model')}</Th>
            </Tr>
          </THead>
          <TBody>
            {shown.map(({ day, previous }) => {
              const prev = previous ?? day;
              const delta = deltaPercent(day.usd_charged_micros, prev.usd_charged_micros);
              const leadRow = lead.get(day.date);
              return (
                <Tr key={day.date} interactive={false} data-testid={`customer-360-usage-daily-${day.date}`}>
                  <Td mono emphasis>
                    {day.date}
                    {day.date === today ? (
                      <span className="ml-1.5 text-micro text-warn">
                        {t('customer_manager_360.usage.daily.in_progress')}
                      </span>
                    ) : null}
                  </Td>
                  <Td num>{TOKEN_FORMATTER.format(day.request_count)}</Td>
                  <Td num>{formatCompact(day.input_tokens)}</Td>
                  <Td num>{formatCompact(day.output_tokens)}</Td>
                  <Td num>
                    {formatUsdMicrosOrCentsAsCnyDisplay(
                      day.usd_charged_micros,
                      day.usd_charged_cents,
                      cnyPerUsd,
                      false,
                    )}
                  </Td>
                  {compare ? (
                    <Td num>
                      {formatUsdMicrosOrCentsAsCnyDisplay(
                        prev.usd_charged_micros,
                        prev.usd_charged_cents,
                        cnyPerUsd,
                        false,
                      )}
                    </Td>
                  ) : null}
                  {compare ? (
                    <Td
                      num
                      className={cx(
                        delta === null ? 'text-paper-low' : delta >= 0 ? 'text-ok' : 'text-bad',
                      )}
                    >
                      {delta === null ? '—' : formatDelta(delta)}
                    </Td>
                  ) : null}
                  <Td>
                    {leadRow === undefined ? (
                      <span className="text-paper-low">—</span>
                    ) : (
                      <span className="text-paper-low">
                        {leadRow.model}
                        <span className="text-paper-low"> · {leadRow.share.toFixed(0)}%</span>
                      </span>
                    )}
                  </Td>
                </Tr>
              );
            })}
            {hiddenCount > 0 ? (
              <Tr interactive={false}>
                <Td colSpan={compare ? 8 : 6}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    data-testid="customer-360-usage-daily-expand"
                    onClick={() => {
                      setExpanded(true);
                    }}
                  >
                    {t(copy.expand, { count: hiddenCount })}
                  </Button>
                </Td>
              </Tr>
            ) : null}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}
